package ui.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.*
import ui.components.M3FilledButton
import ui.components.M3OutlinedButton
import ui.components.Navbar
import kotlin.time.Duration.Companion.milliseconds
import ui.components.Footer as SiteFooter

@Composable
fun LandingScreen(
    onNavigateToTerms: () -> Unit,
    onNavigateToPrivacy: () -> Unit,
    onNavigateToAboutUs: () -> Unit,
    onNavigateToExplore: () -> Unit,
    onNavigateToNews: () -> Unit
) {
    var showInfoModal by remember { mutableStateOf(false) }
    var showComingSoonModal by remember { mutableStateOf(false) }
    var showScrollTop by remember { mutableStateOf(false) }

    // Lógica para mostrar/ocultar el FAB según el scroll
    LaunchedEffect(Unit) {
        kotlinx.browser.window.addEventListener("scroll", {
            showScrollTop = kotlinx.browser.window.scrollY > 500
        })
    }

    Div(attrs = {
        classes("min-h-screen", "bg-gray-50", "dark:bg-medical-bgDark", "transition-colors", "duration-300")
        id("inicio")
    }) {
        Navbar(
            onNavigateToAboutUs = onNavigateToAboutUs,
            onNavigateToExplore = onNavigateToExplore,
            onNavigateToNews = onNavigateToNews,
            onDownloadClick = { 
                kotlinx.browser.window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank")
            }
        )

        // --- HERO SECTION ---
        Main(attrs = {
            classes("pt-32", "pb-16", "px-4", "overflow-hidden")
        }) {
            Div(attrs = {
                // Cambiamos a row desde md (768px) para evitar que se apilen y se monten en tablets
                classes("max-w-7xl", "mx-auto", "flex", "flex-col", "md:flex-row", "items-center", "gap-12", "lg:gap-16")
            }) {
                // Text Content
                Div(attrs = {
                    classes("flex-1", "text-left")
                }) {
                    H1(attrs = {
                        classes("text-4xl", "md:text-6xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "leading-tight", "mb-6")
                    }) {
                        Text("Evoluciona tu práctica médica con ")
                        Span(attrs = { 
                            classes("font-black")
                            style { property("color", "#80CBC4") }
                        }) { Text("documentos digitales seguros") }
                    }
                    
                    P(attrs = {
                        classes("text-xl", "text-gray-600", "dark:text-gray-400", "leading-relaxed", "mb-10", "max-w-2xl")
                    }) {
                        Text("Genera informes, recipes, paraclinicos y reposos en segundos. Con firma digital y validación QR para máxima seguridad en cada consulta.")
                    }

                    Div(attrs = {
                        classes("flex", "flex-col", "sm:flex-row", "items-center", "gap-6", "justify-start")
                    }) {
                        // 1. QR Code para Desktop/Tablets grandes
                        Div(attrs = { 
                            classes("hidden", "lg:flex", "flex-col", "items-center", "gap-4", "p-6", "bg-white", "dark:bg-medical-surfaceDark", "rounded-[2.5rem]", "shadow-md", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark")
                        }) {
                            Img(
                                src = "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://play.google.com/store/apps/details?id=com.artisancode.drrecipe",
                                attrs = { classes("w-28", "h-28") }
                            )
                            P(attrs = { classes("text-[10px]", "font-bold", "text-gray-400", "uppercase", "tracking-wider") }) { 
                                Text("Escanea para descargar")
                            }
                        }

                        // 2. Banner Play Store
                        A(
                            href = "https://play.google.com/store/apps/details?id=com.artisancode.drrecipe",
                            attrs = {
                                attr("target", "_blank")
                                classes("inline-block", "transition-transform", "hover:scale-105", "cursor-pointer")
                            }
                        ) {
                            Img(
                                src = "disponible_play.svg",
                                attrs = { 
                                    classes("h-16", "w-auto")
                                    attr("alt", "Disponible en Google Play")
                                }
                            )
                        }
                    }
                }

                // --- MULTI-DEVICE MOCKUP (CROSSFADE + INTERACTIVE HOVER) ---
                var showFold by remember { mutableStateOf(false) }
                var isFoldHovered by remember { mutableStateOf(false) }

                // Timer automático más relajado (6 segundos)
                LaunchedEffect(Unit) {
                    while (true) {
                        delay(6000.milliseconds)
                        showFold = !showFold
                        if (!showFold) {
                            isFoldHovered = false // Resetear hover al volver a la tablet
                        }
                    }
                }

                Div(attrs = {
                    classes("flex-1", "relative", "w-full", "max-w-xl", "lg:max-w-2xl", "aspect-[4/3]", "md:aspect-[4/3]", "lg:aspect-[16/10]", "mt-12", "md:mt-0")
                }) {
                    // CONTENEDOR 1: TABLET + CELULAR (ESTÁNDAR)
                    Div(attrs = {
                        classes("absolute", "inset-0", "transition-all", "duration-1000", "ease-in-out", "flex", "items-center")
                        style {
                            property("opacity", if (showFold) "0" else "1")
                            property("visibility", if (showFold) "hidden" else "visible")
                            property("transform", if (showFold) "scale(0.95)" else "scale(1)")
                            property("pointer-events", if (showFold) "none" else "auto")
                        }
                    }) {
                        // Tablet
                        Div(attrs = {
                            classes("absolute", "top-1/2", "-translate-y-1/2", "left-0", "w-full", "z-10", "drop-shadow-[0_20px_35px_rgba(0,0,0,0.15)]", "dark:drop-shadow-[0_25px_25px_rgba(255,255,255,0.05)]", "transition-transform", "hover:scale-[1.01]", "duration-500")
                        }) {
                            Img(src = "tablet_frame.webp", attrs = { classes("w-full", "h-auto") })
                        }
                        
                        // Celular
                        Div(attrs = {
                            classes("absolute", "-bottom-[5%]", "-right-[2%]", "w-[27%]", "z-20", "drop-shadow-[0_30px_40px_rgba(0,0,0,0.25)]", "dark:drop-shadow-[0_25px_25px_rgba(255,255,255,0.08)]", "transition-transform", "hover:-translate-y-2", "duration-500")
                        }) {
                            Img(src = "movil_frame.webp", attrs = { classes("w-full", "h-auto") })
                        }
                    }

                    // CONTENEDOR 2: FOLD (CERRADO POR DEFECTO -> SE ABRE AL HOVER)
                    Div(attrs = {
                        classes("absolute", "inset-0", "transition-all", "duration-1000", "ease-in-out", "flex", "items-center", "justify-center", "cursor-pointer")
                        style {
                            property("opacity", if (showFold) "1" else "0")
                            property("visibility", if (showFold) "visible" else "hidden")
                            property("transform", if (showFold) "scale(1)" else "scale(1.05)")
                            property("pointer-events", if (showFold) "auto" else "none")
                        }
                        onMouseEnter { if (showFold) isFoldHovered = true }
                        onMouseLeave { if (showFold) isFoldHovered = false }
                    }) {
                        // Fold Abierto (Oculto por defecto, se muestra al hacer hover)
                        Div(attrs = {
                            classes("absolute", "w-[85%]", "z-10", "drop-shadow-[0_20px_35px_rgba(0,0,0,0.15)]", "dark:drop-shadow-[0_25px_25px_rgba(255,255,255,0.05)]", "transition-all", "duration-700", "ease-in-out")
                            style {
                                property("opacity", if (isFoldHovered) "1" else "0")
                                property("transform", if (isFoldHovered) "scale(1)" else "scale(0.85)")
                                property("visibility", if (isFoldHovered) "visible" else "hidden")
                            }
                        }) {
                            Img(src = "fold_open_frame.webp", attrs = { classes("w-full", "h-auto") })
                        }
                        
                        // Fold Cerrado (Visible por defecto en el centro, se oculta al hacer hover simulando que se abre)
                        Div(attrs = {
                            classes("absolute", "w-[40%]", "z-20", "drop-shadow-[0_30px_40px_rgba(0,0,0,0.25)]", "dark:drop-shadow-[0_25px_25px_rgba(255,255,255,0.08)]", "transition-all", "duration-700", "ease-in-out")
                            style {
                                property("opacity", if (isFoldHovered) "0" else "1")
                                property("transform", if (isFoldHovered) "scale(1.15)" else "scale(1)")
                                property("visibility", if (isFoldHovered) "hidden" else "visible")
                            }
                        }) {
                            Img(src = "fold_closed_frame.webp", attrs = { classes("w-full", "h-auto") })
                            
                            // --- INDICADOR INTERACTIVO (CALL TO ACTION) ---
                            Div(attrs = {
                                classes("absolute", "-right-12", "md:-right-36", "top-1/2", "-translate-y-1/2", "flex", "items-center", "gap-3", "animate-pulse")
                            }) {
                                // Flecha/Cursor (Cambia según dispositivo)
                                Div(attrs = {
                                    classes("w-10", "h-10", "bg-white/90", "dark:bg-medical-surfaceDark/90", "backdrop-blur-md", "rounded-full", "shadow-xl", "flex", "items-center", "justify-center", "border", "border-gray-100", "dark:border-gray-700")
                                }) {
                                    // Icono para Móvil (Manito / Touch)
                                    Span(attrs = { 
                                        classes("material-symbols-outlined", "text-medical-primary", "md:!hidden") 
                                        style { property("font-size", "20px") }
                                    }) { Text("touch_app") }
                                    
                                    // Icono para Desktop (Cursor / Flecha)
                                    Span(attrs = { 
                                        classes("material-symbols-outlined", "text-medical-primary", "!hidden", "md:!block") 
                                        style { property("font-size", "20px") }
                                    }) { Text("ads_click") }
                                }
                                // Etiqueta de texto
                                Div(attrs = {
                                    classes("hidden", "md:block", "bg-gray-900/80", "dark:bg-medical-bgDark/80", "backdrop-blur-sm", "text-white", "text-xs", "font-medium", "px-3", "py-1.5", "rounded-xl", "shadow-lg", "whitespace-nowrap")
                                }) {
                                    Text("Pasa el cursor")
                                }
                            }
                        }
                    }
                }
            }
        }

        // --- FEATURES SECTION ---
        Section(attrs = {
            classes("relative", "py-32", "overflow-hidden")
            id("funciones")
        }) {
            // IMAGEN PARA DESKTOP (Horizontal/Fixed)
            Div(attrs = {
                classes("hidden", "md:block", "absolute", "inset-0", "z-0")
                style {
                    property("background-image", "url('background_web3.png')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("background-attachment", "fixed")
                }
            })

            // IMAGEN PARA MÓVIL (Técnica "Magic Window": Fondo 100% estático sin errores de zoom)
            Div(attrs = {
                classes("md:hidden", "absolute", "inset-0", "z-0")
                style {
                    // Crea un área de recorte que permite ver el elemento fijo solo dentro de esta sección
                    property("clip-path", "inset(0)")
                }
            }) {
                Div(attrs = {
                    // Elemento fijo al visor del teléfono
                    classes("fixed", "inset-0", "w-full", "h-full")
                    style {
                        property("background-image", "url('funciones_mobile.webp')")
                        property("background-size", "cover")
                        property("background-position", "center")
                        property("z-index", "-1")
                    }
                })
            }

            // Overlay sutil
            Div(attrs = {
                classes("absolute", "inset-0", "bg-medical-dark/20", "backdrop-brightness-90", "z-1")
            })

            Div(attrs = {
                classes("relative", "max-w-7xl", "mx-auto", "px-4", "z-10")
            }) {
                // Header Card con efecto Blur (Ajustado para resaltar los cards de funciones)
                Div(attrs = { 
                    classes(
                        "lg:text-left", "text-center", "mb-12", "p-6", "md:p-10", 
                        "bg-white/30", "backdrop-blur-lg", "rounded-[2rem]", 
                        "border", "border-white/10", "shadow-xl", "max-w-3xl"
                    ) 
                }) {
                    Span(attrs = { 
                        classes(
                            "inline-block", "px-3", "py-1", "rounded-full", 
                            "bg-medical-primary/20", "text-medical-primary", "font-bold", 
                            "uppercase", "tracking-widest", "text-[10px]", "mb-4", 
                            "border", "border-medical-primary/30"
                        ) 
                    }) {
                        Text("Descubre")
                    }
                    H2(attrs = { 
                        classes("text-2xl", "md:text-4xl", "font-extrabold", "text-gray-900", "leading-tight")
                    }) {
                        Text("Funciones diseñadas para ")
                        Br(attrs = { classes("hidden", "md:block") })
                        Span(attrs = { 
                            classes("font-black")
                            style { property("color", "#80CBC4") }
                        }) { Text("potenciar tu consulta") }
                    }
                }

                Div(attrs = {
                    classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-8", "justify-items-center")
                }) {
                    FeatureCard("Marca Personal", "Personaliza tus PDFs con tu propio logo, colores corporativos y fuentes profesionales.", "palette")
                    FeatureCard("Seguridad QR", "Cada PDF incluye un código QR único para validación instantánea.", "qr_code_2")
                    FeatureCard("Nube Sincronizada", "Accede a tus consultas desde cualquier dispositivo móvil, tablet o foldables.", "cloud_done")
                    FeatureCard("Firma Digital", "Configura tu sello una vez y firma profesionalmente cada documento.", "draw")
                    FeatureCard("Médicos Verificados", "Uso exclusivo para profesionales de la salud debidamente matriculados.", "verified_user")
                }
            }
        }

        // --- HOW IT WORKS SECTION ---
        Section(attrs = {
            classes("py-24", "bg-white", "dark:bg-medical-primaryDark")
            id("proceso")
        }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4") }) {
                Div(attrs = { classes("text-center", "mb-16") }) {
                    Span(attrs = { 
                        classes("text-medical-primary", "dark:text-medical-bgDark/80", "font-bold", "uppercase", "tracking-widest", "text-xs") 
                    }) { Text("Proceso Simple") }
                    H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "mt-4") }) {
                        Text("Evoluciona tu consulta en 3 pasos")
                    }
                }

                Div(attrs = {
                    classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-12", "relative")
                }) {
                    // Flechas conectoras (Ajuste Pro: centradas con el icono h-20 -> top-10)
                    Div(attrs = { 
                        classes("hidden", "lg:block", "absolute", "top-10", "left-[26%]", "w-[15%]", "h-px", "border-t-2", "border-dashed", "border-medical-primary") 
                    })
                    Div(attrs = { 
                        classes("hidden", "lg:block", "absolute", "top-10", "left-[59%]", "w-[15%]", "h-px", "border-t-2", "border-dashed", "border-medical-primary")
                    })

                    StepItem(
                        number = "01",
                        icon = "edit_note",
                        title = "Genera el documento",
                        description = "Crea recipes, paraclinicos, informes o reposos en segundos con una interfaz optimizada para el flujo médico.",
                        delay = "0s"
                    )
                    StepItem(
                        number = "02",
                        icon = "history_edu",
                        title = "Firma Digitalmente",
                        description = "Tu firma y sello profesional se aplican automáticamente, garantizando validez en cada documento.",
                        delay = "0.2s"
                    )
                    StepItem(
                        number = "03",
                        icon = "qr_code_scanner",
                        title = "Valida y Entrega",
                        description = "El paciente recibe un PDF con código QR único que farmacias y entidades pueden verificar al instante.",
                        delay = "0.4s"
                    )
                }
            }
        }

        // --- TESTIMONIALS SECTION ---
        Section(attrs = {
            classes("py-24", "bg-medical-primary/5", "dark:bg-medical-bgDark")
            id("testimonios")
        }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4") }) {
                Div(attrs = { classes("text-center", "mb-16") }) {
                    H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark") }) {
                        Text("Confianza avalada por la comunidad médica")
                    }
                    P(attrs = { classes("text-gray-500", "dark:text-gray-400", "mt-4", "max-w-2xl", "mx-auto") }) {
                        Text("Únete a los profesionales que ya han transformado su consulta con Dr. Recipe.")
                    }
                }

                Div(attrs = {
                    classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-8")
                }) {
                    TestimonialCard(
                        quote = "Para mí la clave es la libertad. No tengo que esperar a llegar al consultorio para prender la computadora y hacer un informe. Lo resuelvo ahí mismo desde el celular mientras voy de camino o desde casa. Me quita un peso de encima poder cumplirle al paciente rápido y sin vueltas.",
                        author = "Dr. González",
                        specialty = "Pediatría",
                        initials = "G"
                    )
                    TestimonialCard(
                        quote = "Me pasaba mucho que me pedían un récipe por WhatsApp y terminarlo a mano o en Word era tedioso. Con la app lo saco en un minuto y queda impecable, con mi sello y todo. Es súper práctico porque no me obliga a estar amarrada a un escritorio para ser profesional.",
                        author = "Dra. Martínez",
                        specialty = "Medicina General",
                        initials = "M"
                    )
                    TestimonialCard(
                        quote = "Buscaba algo sencillo que no me complicara la vida con mil funciones que no uso. Dr. Recipe va al grano: crear el documento, ponerle el QR de seguridad y enviarlo. Es la herramienta que siempre tengo a mano para cuando necesito resolver una orden o un reposo rápido.",
                        author = "Od. Herrera",
                        specialty = "Odontología",
                        initials = "H"
                    )
                }
            }
        }

        // --- FAQ SECTION ---
        Section(attrs = {
            classes("py-12", "md:py-20", "bg-white", "dark:bg-medical-primaryDark")
            id("preguntas")
        }) {
            Div(attrs = { classes("max-w-3xl", "mx-auto", "px-4") }) {
                Div(attrs = { classes("text-center", "mb-10", "md:mb-12") }) {
                    Span(attrs = { 
                        classes("text-medical-primary", "dark:text-medical-bgDark/80", "font-bold", "uppercase", "tracking-widest", "text-xs") 
                    }) { Text("Soporte") }
                    H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "mt-2") }) {
                        Text("Preguntas Frecuentes")
                    }
                }

                Div(attrs = { classes("space-y-4") }) {
                    FaqItem(
                        question = "¿Los documentos tienen validez legal?",
                        answer = { Text("Sí. Nuestros documentos cumplen con los estándares de firma digital y validación mediante código QR, permitiendo que farmacias y entidades verifiquen la autenticidad del documento original en tiempo real.") }
                    )
                    FaqItem(
                        question = "¿Qué sucede si pierdo mi dispositivo?",
                        answer = { Text("Tu información está segura. Al estar sincronizada con la nube de Dr. Recipe, solo necesitas iniciar sesión en un nuevo dispositivo para recuperar todo tu historial médico y plantillas.") }
                    )
                    FaqItem(
                        question = "¿Es seguro el almacenamiento de datos?",
                        answer = { Text("Utilizamos cifrado de nivel bancario y cumplimos con las normativas de privacidad más estrictas. Solo tú, como médico verificado, tienes acceso a la información de tus pacientes.") }
                    )
                    FaqItem(
                        question = "¿Puedo usar la App sin conexión a internet?",
                        answer = {
                            Text("Sí. Puedes registrar pacientes y generar todos tus récipes o informes PDF de forma local y sin conexión. Sin embargo, ten en cuenta que para que el código QR sea verificable por farmacias o terceros a través de nuestra web, ")
                            B { Text("tu dispositivo deberá haberse sincronizado con la nube (al recuperar conexión) ") }
                            Text("al menos una vez después de crear el documento.")
                        }
                    )
                    FaqItem(
                        question = "¿Cuenta con seguridad por huella dactilar?",
                        answer = {
                            Text("Sí. Dr. Recipe permite activar la autenticación biométrica para reforzar la privacidad y seguridad de tu práctica. Esta función debe habilitarse desde los ajustes de la App y requiere que tu dispositivo cuente con lector de huella. Una vez activa, se solicitará validación biométrica para el inicio de sesión, gestión de pagos, emisión de documentos PDF (informes, récipes, etc.), así como en la creación o edición de perfiles de pacientes y nuevas consultas.")
                        }
                    )
                }
            }
        }

        // --- PRINTING COMPATIBILITY SECTION ---
        Section(attrs = {
            classes("py-24", "bg-gray-50/50", "dark:bg-medical-bgDark")
            id("impresion")
        }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4") }) {
                Div(attrs = { classes("flex", "flex-col", "lg:flex-row", "items-center", "gap-16") }) {
                    // Imagen/Icono representativo (Lado Izquierdo)
                    Div(attrs = { 
                        classes("flex-1", "relative", "flex", "justify-center") 
                    }) {
                        Div(attrs = {
                            classes("w-64", "h-64", "md:w-80", "md:h-80", "bg-white", "rounded-[3rem]", "shadow-2xl", "flex", "items-center", "justify-center", "relative", "z-10")
                        }) {
                            Span(attrs = { 
                                classes("material-symbols-outlined", "text-medical-primary")
                                style {
                                    // Aseguramos el tamaño con style por si el linter o tailwind no procesan el arbitrary value
                                    property("font-size", "min(40vw, 180px)")
                                }
                            }) { Text("print") }
                            
                            // Elementos flotantes decorativos
                            Div(attrs = { 
                                classes("absolute", "-top-4", "-right-4", "w-16", "h-16", "rounded-2xl", "flex", "items-center", "justify-center", "text-white", "shadow-lg", "animate-bounce") 
                                style { property("background-color", "#80CBC4") }
                            }) {
                                Span(attrs = { classes("material-symbols-outlined") }) { Text("wifi") }
                            }
                            Div(attrs = { classes("absolute", "-bottom-2", "-left-6", "w-14", "h-14", "bg-medical-dark", "rounded-2xl", "flex", "items-center", "justify-center", "text-white", "shadow-lg") }) {
                                Span(attrs = { classes("material-symbols-outlined") }) { Text("bluetooth") }
                            }
                        }
                    }

                    // Contenido de Texto (Lado Derecho)
                    Div(attrs = { classes("flex-1", "text-center", "lg:text-left") }) {
                        Span(attrs = { 
                            classes("text-medical-primary", "dark:text-medical-primaryDark", "font-bold", "uppercase", "tracking-widest", "text-xs") 
                        }) { Text("Conectividad Total") }
                        H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mt-4", "mb-6") }) {
                            Text("Impresión sin límites y entrega digital")
                        }
                        P(attrs = { classes("text-lg", "text-gray-600", "dark:text-gray-400", "mb-8") }) {
                            Text("Sabemos que el papel sigue siendo importante. Genera PDFs universales optimizados para imprimir desde cualquier dispositivo móvil o tablet vía Wi-Fi, Bluetooth o mediante el envío directo a centros de impresión.")
                        }

                        Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "gap-6") }) {
                            PrintFeature("Impresión Inalámbrica", "Compatible con impresoras Wi-Fi y Bluetooth.", "settings_remote")
                            PrintFeature("PDF de Alta Calidad", "Documentos vectoriales nítidos y profesionales.", "picture_as_pdf")
                            PrintFeature("Envío Instantáneo", "Comparte por WhatsApp, Email o Telegram.", "share")
                            PrintFeature("Formato Universal", "Optimizado para hojas tamaño A4 y Carta.", "description")
                        }
                    }
                }
            }
        }

        // --- PRICING SECTION ---
        Section(attrs = {
            classes("py-24", "bg-white", "dark:bg-medical-primaryDark")
            id("precios")
        }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4") }) {
                Div(attrs = { classes("text-center", "mb-16") }) {
                    Span(attrs = { classes("text-medical-primary", "dark:text-medical-bgDark/80", "font-bold", "uppercase", "tracking-widest", "text-sm") }) {
                        Text("Planes")
                    }
                    H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "mt-4") }) {
                        Text("Invierte en tu tranquilidad profesional")
                    }
                    P(attrs = { classes("text-gray-500", "dark:text-medical-bgDark/80", "mt-4", "max-w-2xl", "mx-auto") }) {
                        Text("Elige el plan que mejor se adapte a tu ritmo de consulta. Gestiona todo de forma segura desde la App.")
                    }
                }

                val commonFeatures = listOf(
                    "Consultas médicas ilimitadas",
                    "Marca personal profesional",
                    "Firma y Sello digital",
                    "Sincronización en la nube",
                    "Validación QR segura",
                    "Acceso a nuevas funciones"
                )

                Div(attrs = { classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-8") }) {
                    // Plan Trial
                    PricingCard(
                        title = "Prueba Gratuita",
                        price = "0",
                        period = "15 días",
                        features = listOf("Todas las funciones activas", "Sin necesidad de tarjetas", "Ideal para empezar"),
                        buttonText = "Empezar ahora",
                        isPopular = false,
                        onButtonClick = { 
                            kotlinx.browser.window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank") 
                        }
                    )

                    // Plan Mensual
                    PricingCard(
                        title = "Plan Mensual",
                        price = "5.99",
                        period = "mes",
                        features = commonFeatures,
                        buttonText = "Elegir Plan",
                        isPopular = true,
                        onButtonClick = { 
                            kotlinx.browser.window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank") 
                        }
                    )

                    // Plan Anual
                    PricingCard(
                        title = "Plan Anual",
                        price = "59.99",
                        period = "año",
                        features = commonFeatures + "Ahorra 2 meses",
                        buttonText = "Elegir Plan",
                        isPopular = false,
                        onButtonClick = { 
                            kotlinx.browser.window.open("https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", "_blank") 
                        }
                    )
                }
                
                // --- PAYMENT INFO FOR VENEZUELA ---
                Div(attrs = { 
                    classes("mt-16", "p-8", "bg-gray-50", "dark:bg-medical-surfaceDark", "rounded-[2.5rem]", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark", "max-w-4xl", "mx-auto")
                }) {
                    Div(attrs = { classes("flex", "flex-col", "md:flex-row", "items-center", "gap-8") }) {
                        // Logo Google Pay en lugar del icono de billete
                        Div(attrs = { 
                            classes("w-21", "h-21", "bg-white", "dark:bg-gray-800", "rounded-3xl", "shadow-sm", "flex", "items-center", "justify-center", "shrink-0", "p-4")
                        }) {
                            Img(
                                src = "google-pay-mark_800.svg",
                                attrs = { 
                                    classes("w-full", "h-auto")
                                    attr("alt", "Google Pay")
                                }
                            )
                        }
                        
                        Div(attrs = { classes("text-center", "md:text-left") }) {
                            H3(attrs = { classes("text-xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-2") }) {
                                Text("¿Cómo pagar en Venezuela?")
                            }
                            P(attrs = { classes("text-gray-600", "dark:text-gray-400", "leading-relaxed") }) {
                                Text("El proceso es simple y seguro a través de ")
                                B { Text("Google Play Store") }
                                Text(". Solo debes vincular tu ")
                                B { Text("tarjeta internacional o tarjeta de débito en dólares") }
                                Text(" de tu banco nacional a tu cuenta de Google. Es la vía más confiable para activar tu suscripción al instante.")
                            }
                        }
                    }
                }

                P(attrs = { classes("text-center", "text-gray-400", "dark:text-gray-500", "text-xs", "mt-12") }) {
                    Text("* Los pagos y suscripciones se gestionan de forma segura a través de Google Play Store.")
                }
            }
        }

        // --- SECURITY SECTION ---
        Section(attrs = {
            classes("py-24", "bg-medical-primary/5", "dark:bg-medical-primaryDark/5")
            id("seguridad")
        }) {
            Div(attrs = {
                classes("max-w-7xl", "mx-auto", "px-4")
            }) {
                Div(attrs = {
                    classes("flex", "flex-col", "md:flex-row", "items-center", "gap-16")
                }) {
                    Div(attrs = { classes("flex-1") }) {
                        Span(attrs = { classes("text-medical-primary", "dark:text-medical-primaryDark", "font-bold", "tracking-widest", "uppercase", "text-sm") }) {
                            Text("Privacidad Garantizada")
                        }
                        H2(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mt-4", "mb-6") }) {
                            Text("Tu información y la de tus pacientes está protegida")
                        }
                        Div(attrs = { classes("space-y-4") }) {
                            SecurityItem("Cifrado de extremo a extremo en la nube.")
                            SecurityItem("Cumplimiento con estándares de protección de datos.")
                            SecurityItem("Acceso restringido solo al médico titular.")
                        }
                    }
                    Div(attrs = { classes("flex-1", "bg-white/60", "dark:bg-medical-surfaceDark/60", "backdrop-blur-lg", "p-8", "rounded-3xl", "shadow-xl", "border", "border-medical-primary/10", "dark:border-medical-primaryDark/10") }) {
                        H3(attrs = { classes("text-xl", "font-bold", "dark:text-medical-onSurfaceDark", "mb-4") }) { Text("Verificación para Farmacias") }
                        P(attrs = { classes("text-gray-600", "dark:text-gray-400", "mb-6") }) {
                            Text("Eliminamos el riesgo de falsificaciones. Al escanear el QR, el sistema muestra el documento original firmado por usted.")
                        }
                        M3OutlinedButton(attrs = {
                            onClick { showInfoModal = true }
                        }) { Text("Saber más sobre validación") }
                    }
                }
            }
        }

        // --- FINAL CTA BANNER ---
        Section(attrs = {
            classes("py-20", "bg-medical-primary", "dark:bg-medical-primaryDark")
        }) {
            Div(attrs = {
                classes("max-w-4xl", "mx-auto", "px-4", "text-center")
            }) {
                H2(attrs = { 
                    classes("text-3xl", "md:text-5xl", "font-bold", "text-white", "dark:text-medical-bgDark", "mb-8", "leading-tight") 
                }) {
                    Text("¿Listo para llevar tu práctica médica al siguiente nivel?")
                }
                P(attrs = { 
                    classes("text-white/80", "dark:text-medical-bgDark/80", "text-lg", "md:text-xl", "mb-12", "max-w-2xl", "mx-auto") 
                }) {
                    Text("Únete a cientos de médicos que ya confían en Dr. Recipe para gestionar sus consultas de forma segura y eficiente.")
                }
                
                Div(attrs = { 
                    classes("flex", "flex-col", "sm:flex-row", "items-center", "justify-center", "gap-6") 
                }) {
                    A(
                        href = "https://play.google.com/store/apps/details?id=com.artisancode.drrecipe",
                        attrs = {
                            attr("target", "_blank")
                            classes("inline-block", "transition-transform", "hover:scale-105", "cursor-pointer")
                        }
                    ) {
                        Img(
                            src = "disponible_play.svg",
                            attrs = { 
                                classes("h-20", "w-auto")
                                attr("alt", "Disponible en Google Play")
                            }
                        )
                    }
                    
                    Div(attrs = { 
                        classes("flex", "items-center", "gap-3", "text-white/90", "dark:text-medical-bgDark/90", "text-sm", "font-medium") 
                    }) {
                        Span(attrs = { classes("material-symbols-outlined") }) { Text("verified") }
                        Text("15 días de prueba gratuita")
                    }
                }
            }
        }

        // --- FOOTER ---
        SiteFooter(
            onNavigateToTerms = onNavigateToTerms,
            onNavigateToPrivacy = onNavigateToPrivacy
        )

        // --- INFO MODAL ---
        if (showInfoModal) {
            InfoModal(onClose = { showInfoModal = false })
        }

        // --- COMING SOON MODAL ---
        if (showComingSoonModal) {
            ComingSoonModal(onClose = { showComingSoonModal = false })
        }

        // --- FAB: SCROLL TO TOP ---
        if (showScrollTop) {
            Div(attrs = {
                classes(
                    "fixed", "bottom-8", "right-8", "z-40", 
                    "w-14", "h-14", "bg-medical-primary", "dark:bg-medical-primaryDark", "text-white", "dark:text-medical-bgDark",
                    "rounded-2xl", "shadow-2xl", "flex", "items-center", "justify-center", 
                    "cursor-pointer", "transition-all", "hover:scale-110", "active:scale-95", 
                    "animate-bounce"
                )
                onClick { 
                    kotlinx.browser.window.scrollTo(0.0, 0.0)
                }
            }) {
                Span(attrs = { classes("material-symbols-outlined", "text-3xl") }) { Text("keyboard_arrow_up") }
            }
        }
    }
}

@Composable
private fun InfoModal(onClose: () -> Unit) {
    Div(attrs = {
        classes("fixed", "inset-0", "z-[100]", "flex", "items-center", "justify-center", "p-4", "bg-medical-dark/40", "dark:bg-medical-bgDark/80", "backdrop-blur-sm")
        onClick { onClose() } // Cerrar al hacer clic fuera
    }) {
        Div(attrs = {
            classes("bg-white", "dark:bg-medical-surfaceDark", "rounded-[2.5rem]", "max-w-lg", "w-full", "shadow-2xl", "overflow-hidden", "relative")
            onClick { it.stopPropagation() } // Evitar que el clic dentro del modal lo cierre
        }) {
            // Header con botón cerrar
            Div(attrs = { classes("p-8", "pb-0", "flex", "justify-between", "items-start") }) {
                H3(attrs = { classes("text-2xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark") }) { Text("¿Cómo funciona?") }
                Span(attrs = { 
                    classes("material-symbols-outlined", "cursor-pointer", "text-gray-400", "hover:text-gray-600") 
                    onClick { onClose() }
                }) { Text("close") }
            }

            // Contenido: Pasos
            Div(attrs = { classes("p-8", "space-y-8") }) {
                ModalStep(
                    icon = "qr_code_scanner",
                    title = "1. Escaneo del Código QR",
                    description = "El farmacéutico o entidad escanea el código único presente en el documento físico o digital."
                )
                ModalStep(
                    icon = "cloud_sync",
                    title = "2. Verificación en tiempo real",
                    description = "Nuestra plataforma consulta instantáneamente el registro de documentos para validar su autenticidad."
                )
                ModalStep(
                    icon = "verified",
                    title = "3. Confirmación de Integridad",
                    description = "Se muestra el documento original emitido por el médico, asegurando que los datos no han sido alterados."
                )
            }

            // Footer del Modal
            Div(attrs = { classes("p-8", "bg-gray-50", "dark:bg-medical-surfaceDark", "text-center") }) {
                M3OutlinedButton(attrs = {
                    onClick { onClose() }
                    classes("w-full")
                }) { Text("Entendido") }
            }
        }
    }
}

@Composable
private fun ModalStep(icon: String, title: String, description: String) {
    Div(attrs = { classes("flex", "gap-4") }) {
        Div(attrs = { classes("w-12", "h-12", "bg-medical-primary/10", "dark:bg-medical-primaryDark/20", "rounded-2xl", "flex", "items-center", "justify-center", "shrink-0", "text-medical-primary", "dark:text-medical-primaryDark") }) {
            Span(attrs = { classes("material-symbols-outlined") }) { Text(icon) }
        }
        Div {
            H4(attrs = { classes("font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-1") }) { Text(title) }
            P(attrs = { classes("text-sm", "text-gray-600", "dark:text-gray-400", "leading-relaxed") }) { Text(description) }
        }
    }
}

@Composable
private fun SecurityItem(text: String) {
    Div(attrs = { classes("flex", "items-center", "gap-3") }) {
        Span(attrs = { 
            classes("material-symbols-outlined") 
            style { property("color", "#80CBC4") }
        }) { Text("check_circle") }
        Text(text)
    }
}

@Composable
private fun ComingSoonModal(onClose: () -> Unit) {
    Div(attrs = {
        classes("fixed", "inset-0", "z-[100]", "flex", "items-center", "justify-center", "p-4", "bg-medical-dark/40", "backdrop-blur-sm")
        onClick { onClose() }
    }) {
        Div(attrs = {
            classes("bg-white", "rounded-[2.5rem]", "max-w-md", "w-full", "shadow-2xl", "p-10", "text-center", "relative")
            onClick { it.stopPropagation() }
        }) {
            Span(attrs = {
                classes("material-symbols-outlined", "text-medical-primary", "text-6xl", "mb-6")
            }) { Text("rocket_launch") }
            
            H3(attrs = { classes("text-2xl", "font-bold", "text-gray-900", "mb-4") }) {
                Text("¡Estamos despegando!")
            }
            
            P(attrs = { classes("text-gray-600", "leading-relaxed", "mb-8") }) {
                Text("Dr. Recipe se encuentra en su fase final de revisión por Google Play. Estará disponible para descarga muy pronto.")
            }

            M3FilledButton(attrs = {
                onClick { onClose() }
                classes("w-full")
            }) { Text("Entendido") }
        }
    }
}

@Composable
private fun PricingCard(
    title: String,
    price: String,
    period: String,
    features: List<String>,
    buttonText: String,
    isPopular: Boolean,
    onButtonClick: () -> Unit
) {
    Div(attrs = {
        classes("relative", "p-8", "rounded-[2.5rem]", "border", "transition-all", "hover:shadow-2xl", "flex", "flex-col")
        if (isPopular) {
            classes("border-medical-primary", "shadow-xl", "bg-white", "dark:bg-medical-bgDark", "dark:border-medical-bgDark", "scale-105", "z-10")
        } else {
            classes("border-gray-100", "bg-gray-50/50", "dark:bg-medical-bgDark", "dark:border-medical-bgDark")
        }
    }) {
        if (isPopular) {
            Span(attrs = {
                classes("absolute", "-top-4", "left-1/2", "-translate-x-1/2", "bg-medical-primary", "dark:bg-medical-successDark", "text-white", "dark:text-medical-bgDark", "text-xs", "font-bold", "px-4", "py-1", "rounded-full", "uppercase")
            }) { Text("Más Popular") }
        }

        H3(attrs = { classes("text-xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-4") }) { Text(title) }
        
        Div(attrs = { classes("flex", "items-baseline", "gap-1", "mb-8") }) {
            Span(attrs = { classes("text-4xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark") }) { Text("$$price") }
            Span(attrs = { classes("text-gray-500", "dark:text-gray-400", "text-sm") }) { Text("/$period") }
        }

        Ul(attrs = { classes("space-y-4", "mb-10", "flex-1") }) {
            features.forEach { feature ->
                Li(attrs = { classes("flex", "items-center", "gap-3", "text-sm", "text-gray-600", "dark:text-gray-300") }) {
                    Span(attrs = { classes("material-symbols-outlined", "text-green-500", "dark:text-medical-successDark", "text-lg") }) { Text("check_circle") }
                    Text(feature)
                }
            }
        }

        M3FilledButton(attrs = {
            classes("w-full")
            if (!isPopular) classes("opacity-80")
            onClick { onButtonClick() }
        }) { Text(buttonText) }

        P(attrs = { classes("text-center", "text-[10px]", "text-gray-400", "mt-3") }) {
            Text("Disponible ahora en Google Play")
        }
    }
}

@Composable
private fun TestimonialCard(quote: String, author: String, specialty: String, initials: String) {
    Div(attrs = {
        classes("bg-white", "dark:bg-medical-surfaceDark", "p-8", "rounded-[2.5rem]", "shadow-sm", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark", "flex", "flex-col", "gap-6", "transition-all", "hover:shadow-xl", "hover:-translate-y-1")
    }) {
        // 1. Estrellas de Calificación (Premium Feel)
        Div(attrs = { classes("flex", "gap-1") }) {
            repeat(5) {
                Span(attrs = { 
                    classes("material-symbols-outlined", "text-amber-400", "text-xl")
                    style { property("font-variation-settings", "'FILL' 1") }
                }) { Text("star") }
            }
        }

        // 2. Texto del Testimonio
        P(attrs = { classes("text-gray-700", "dark:text-gray-300", "text-lg", "leading-relaxed", "flex-1", "font-medium") }) {
            Text("\"$quote\"")
        }

        // 3. Info del Autor con Avatar Médico
        Div(attrs = { classes("flex", "items-center", "gap-4", "pt-6", "border-t", "border-gray-50", "dark:border-gray-800/50") }) {
            // Avatar Circular con color terciario
            Div(attrs = { 
                classes("w-14", "h-14", "rounded-full", "flex", "items-center", "justify-center", "border-2", "relative", "bg-medical-tertiary", "dark:bg-medical-successDark", "border-medical-tertiary", "dark:border-medical-successDark") 
            }) {
                Span(attrs = { classes("text-white", "dark:text-medical-bgDark", "font-bold", "text-lg") }) {
                    Text(initials)
                }
            }
            Div {
                P(attrs = { classes("font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "text-base") }) { Text(author) }
                P(attrs = { classes("text-xs", "text-medical-primary", "dark:text-medical-primaryDark", "font-semibold", "uppercase", "tracking-wider") }) { Text(specialty) }
            }
        }
    }
}

@Composable
private fun PrintFeature(title: String, description: String, icon: String) {
    Div(attrs = { classes("flex", "gap-4", "items-start") }) {
        Div(attrs = { classes("w-10", "h-10", "bg-medical-primary/10", "rounded-xl", "flex", "items-center", "justify-center", "shrink-0", "text-medical-primary") }) {
            Span(attrs = { classes("material-symbols-outlined", "text-xl") }) { Text(icon) }
        }
        Div {
            H4(attrs = { classes("font-bold", "text-gray-900", "text-sm") }) { Text(title) }
            P(attrs = { classes("text-xs", "text-gray-500", "leading-tight") }) { Text(description) }
        }
    }
}

@Composable
private fun FaqItem(question: String, answer: @Composable () -> Unit) {
    var isOpen by remember { mutableStateOf(false) }

    Div(attrs = {
        classes("border", "border-gray-100", "dark:border-medical-bgDark/20", "rounded-3xl", "overflow-hidden", "transition-all")
        if (isOpen) classes("bg-gray-50", "dark:bg-medical-bgDark/10", "border-medical-primary/20", "dark:border-medical-bgDark/40") else classes("bg-white", "dark:bg-medical-bgDark/40")
    }) {
        // Pregunta (Header clickable)
        Div(attrs = {
            classes("p-6", "flex", "justify-between", "items-center", "cursor-pointer", "select-none")
            onClick { isOpen = !isOpen }
        }) {
            H3(attrs = { 
                classes("text-base", "md:text-lg", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "pr-8") 
            }) { Text(question) }
            
            Span(attrs = { 
                classes("material-symbols-outlined", "text-medical-primary", "dark:text-medical-bgDark", "transition-transform", "duration-300")
                if (isOpen) style { property("transform", "rotate(180deg)") }
            }) { Text("expand_more") }
        }

        // Respuesta (Contenido colapsable)
        if (isOpen) {
            Div(attrs = { 
                classes("px-6", "pb-6", "text-gray-600", "dark:text-medical-bgDark/80", "text-sm", "md:text-base", "leading-relaxed", "animate-fadeIn")
            }) {
                answer()
            }
        }
    }
}

@Composable
private fun StepItem(number: String, icon: String, title: String, description: String, delay: String) {
    Div(attrs = {
        classes("flex", "flex-col", "items-center", "text-center", "reveal")
        style { property("animation-delay", delay) }
    }) {
        Div(attrs = { classes("relative", "mb-6") }) {
            // Círculo del número (Badge)
            Span(attrs = { 
                classes("absolute", "-top-2", "-right-2", "w-8", "h-8", "bg-medical-tertiary", "dark:bg-medical-bgDark", "text-white", "dark:text-medical-primaryDark", "rounded-full", "flex", "items-center", "justify-center", "font-bold", "text-xs", "shadow-lg", "z-10") 
            }) { Text(number) }
            
            // Círculo del Icono
            Div(attrs = { 
                classes("w-20", "h-20", "bg-medical-primary/5", "dark:bg-medical-bgDark/10", "rounded-[2rem]", "flex", "items-center", "justify-center", "text-medical-primary", "dark:text-medical-bgDark", "border", "border-medical-primary/10", "dark:border-medical-bgDark/20") 
            }) {
                Span(attrs = { classes("material-symbols-outlined", "text-4xl") }) { Text(icon) }
            }
        }
        
        H3(attrs = { classes("text-xl", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "mb-3") }) { Text(title) }
        P(attrs = { classes("text-gray-600", "dark:text-medical-bgDark/80", "text-sm", "leading-relaxed", "max-w-[250px]") }) { Text(description) }
    }
}

@Composable
private fun FeatureCard(title: String, description: String, icon: String) {
    Div(attrs = {
        classes("h-full", "p-10", "bg-white/60", "backdrop-blur-lg", "rounded-3xl", "border", "border-gray-100", "hover:shadow-xl", "transition-all", "hover:-translate-y-1")
    }) {
        Div(attrs = {
            classes("w-14", "h-14", "rounded-2xl", "flex", "items-center", "justify-center", "mb-6", "text-white")
            style { property("background-color", "#80CBC4") }
        }) {
            Span(attrs = { classes("material-symbols-outlined", "text-3xl") }) { Text(icon) }
        }
        H3(attrs = { classes("text-xl", "font-bold", "text-gray-900", "mb-3") }) { Text(title) }
        P(attrs = { classes("text-gray-600", "leading-relaxed") }) { Text(description) }
    }
}
